import { FunctionComponent, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { faCircleMinus } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Toggle } from "@/components/Toggle/Toggle";
import { classNames } from "@/utils/classNames";

import { InlineName } from "./InlineName";

interface DetailHeaderProps {
	name: string;
	active: boolean;
	onToggle?: (active: boolean) => void;
	onRename?: (name: string) => void;
	/** What the toggle tooltip calls the row: segment, persona, lead scoring, criterion. */
	activeTitle: string;
	inactiveTitle: string;
	actions?: ReactNode;
	testId?: string;
	/** Bump to open the name editor (wired to a "Rename" action). */
	renameSignal?: number;
}

/**
 * The header row of a detail card (PREC-4055): the activate toggle, the name
 * (editable in place), a "deactivated" label while it is off, and the row's
 * actions. Shared by segments, personas, lead scorings and criteria.
 */
export const DetailHeader: FunctionComponent<DetailHeaderProps> = ({
	name,
	active,
	onToggle,
	onRename,
	activeTitle,
	inactiveTitle,
	actions,
	testId,
	renameSignal,
}) => {
	const { t } = useTranslation();

	return (
		<div
			className={classNames(
				"flex items-center gap-2.5 border-b border-border-light px-3.5 py-2.5",
				!active && "bg-background-warning"
			)}
			data-testid={testId}
		>
			{onToggle && (
				<span title={active ? activeTitle : inactiveTitle}>
					<Toggle
						isChecked={active}
						testId={testId ? `${testId}-toggle` : undefined}
						onChange={() => onToggle(!active)}
					/>
				</span>
			)}
			{onRename ? (
				<InlineName
					className="text-sm font-semibold text-text"
					editSignal={renameSignal}
					testId={testId ? `${testId}-name` : undefined}
					value={name}
					onChange={onRename}
				/>
			) : (
				<span className="text-sm font-semibold text-text">{name}</span>
			)}
			{!active && (
				<span className="flex h-[22px] items-center gap-1.5 rounded-md border border-tag-warning-border bg-background-warning px-2 text-[11px] font-medium text-tag-warning-dark">
					<Icon className="h-3 w-3" icon={faCircleMinus} />
					{t("leadScoring.detail.deactivated")}
				</span>
			)}
			{actions}
		</div>
	);
};
